import dayjs from 'dayjs'
import { createSignal, createMemo, createEffect, onMount, onCleanup, For } from 'solid-js'
import { events, GAME_INFO, GAME_STARTED, BET, BET_DONE, BET_CANCEL } from '@/store/messenger'
import { EllipseOutline, CloseCircleOutline, CheckmarkCircleOutline, CheckmarkDoneCircleOutline } from '@/icons'
import account from '@/store/account'
import { res2json, hourPadStart } from '@/utils'
import { store } from '@/store'

const [bets, setBets] = createSignal([])

export default function Live() {
  const [loading, setLoading] = createSignal(true)
  const [gameInfo, setGameInfo] = createSignal({})
  const robotsBetAmount = createMemo(() => bets().filter(e => e.is_robot).map(e => e.amount).reduce((l, r) => l + r, 0))
  const playersBetAmount = createMemo(() => bets().filter(e => !e.is_robot).map(e => e.amount).reduce((l, r) => l + r, 0))
  const validBetAmount = createMemo(() => bets().filter(e => !e.is_robot && e.status === 'betted').map(e => e.amount).reduce((l, r) => l + r, 0))
  const players = createMemo(() => bets().filter(e => !e.is_robot))
  const robots = createMemo(() => bets().filter(e => e.is_robot))
  const [minutes, setMinutes] = createSignal(0)
  const [seconds, setSeconds] = createSignal(0)

  onMount(() => {
    events.on(GAME_INFO, onGameInfo)
    events.on(GAME_STARTED, onGameStarted)
    events.on(BET, onBetStatusUpdated)
    events.on(BET_DONE, onBetStatusUpdated)
    events.on(BET_CANCEL, onBetStatusUpdated)

    fetch(`/api/${account().game_code}/betting/current`).then(res2json).then(r => { setBets(r.data ?? []) }).finally(() => { setLoading(false) })
  })
  onCleanup(() => {
    events.removeListener(BET_CANCEL, onBetStatusUpdated)
    events.removeListener(BET_DONE, onBetStatusUpdated)
    events.removeListener(BET, onBetStatusUpdated)
    events.removeListener(GAME_STARTED, onGameStarted)
    events.removeListener(GAME_INFO, onGameInfo)
  })

  const fetchGameInfo = () => fetch(`/api/${account().game_code}/current`).then(res2json).then(r => { setGameInfo(r.data ?? {}) })
  createEffect(() => {
    if (store.gameAccount.username) {
      fetchGameInfo()
    }
  })

  let lastGameNum = ''
  function onGameInfo(data) {
    if (data.game === account().game) {
      setGameInfo(data ?? {})
      if (lastGameNum !== data?.num) {
        lastGameNum = data?.num
      }
    }
  }
  function onGameStarted() {
    setBets([])
  }
  let fetchingBetStatus = false
  function onBetStatusUpdated(data) {
    if (fetchingBetStatus) {
      return
    }
    fetchingBetStatus = true
    setTimeout(() => {
      fetch(`/api/${account().game_code}/betting/current`).then(res2json).then(r => { setBets(r.data ?? []) }).finally(() => { fetchingBetStatus = false })
    }, 50)
  }

  let timer
  onMount(() => {
    timer = setInterval(() => updateCountdown(), 1000)
  })
  onCleanup(() => {
    clearInterval(timer)
  })
  function updateCountdown() {
    const now = dayjs()
    const closeAt = dayjs(gameInfo().close_time).subtract(store.reservedCloseTime, 's')
    const isActive = dayjs(gameInfo().open_time) <= now && closeAt >= now
    const diff = closeAt.diff(now, 's')
    setMinutes(isActive ? Math.floor(diff / 60) : 0)
    setSeconds(isActive ? diff - minutes() * 60 : 0)
  }

  return (
    <>
      <div class="flex flex-col gap-4">
        <div class="breadcrumbs text-sm">
          <ul>
            <li><a href="/home">后台首页</a></li>
            <li>房间动态</li>
          </ul>
        </div>
        <div class="flex flex-col md:flex-row md:items-center md:justify-between">
          <div class="flex items-center gap-2">
            <span>下注榜: {gameInfo().num} 期</span>
            <span>倒数 <span class="text-error">{hourPadStart(minutes())}</span> 分 <span class="text-error">{hourPadStart(seconds())}</span> 秒</span>
          </div>
          <div>普: {(playersBetAmount() / 100).toFixed(2)}, 托: {(robotsBetAmount() / 100).toFixed(2)}</div>
        </div>
        <div class="overflow-x-auto">
          <table class="table whitespace-nowrap">
            <thead>
              <tr class="bg-base-200">
                <th class="px-2 lg:px-3">状态</th>
                <th class="px-2 lg:px-3">指令</th>
                <th class="px-2 lg:px-3">合计</th>
                <th class="px-2 lg:px-3">玩家</th>
                <th class="px-2 lg:px-3">类型</th>
              </tr>
            </thead>
            <tbody>
              <For each={bets() ?? []}>
                {(row) => (
                  <tr class="hover:bg-base-200">
                    <td class="px-2 lg:px-3"><BetStatusBadge row={row} /></td>
                    <td class="px-2 lg:px-3">{row.bet}</td>
                    <td class="px-2 lg:px-3">{(row.amount / 100).toFixed(2)}</td>
                    <td class="px-2 lg:px-3">{row.username}({row.nickname})</td>
                    <td class="px-2 lg:px-3">{row.is_robot ? '机/托' : '玩家'}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </div>

      <Portal mount={document.body}>
        {loading() && (<div class="fixed left-2 right-2 bottom-0"><progress class="progress progress-success w-full"></progress></div>)}
      </Portal>
    </>
  )
}

function BetStatusBadge({ row }) {
  return <>
    {row.is_robot ? <>
      {row.status === 'betted' && <div class="w-6 text-[#d5f3f7]"><CheckmarkDoneCircleOutline /></div>}
    </> : <>
      {row.status === 'betted' && <div class="w-6 text-[#0c8844]"><CheckmarkDoneCircleOutline /></div>}
      {row.status === 'created' && <div class="w-6 text-[#ff8c00]"><CheckmarkCircleOutline /></div>}
      {row.status === 'failed' && <div class="w-6 text-[#ff0000]"><CloseCircleOutline /></div>}
      {row.status === 'cancelled' && <span>[退]</span>}
    </>}
  </>
}
